<script setup>
import { Link, useForm } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Loading from 'vue-loading-overlay';
import { ref, onMounted } from "vue";
import FlatPickr from 'vue-flatpickr-component';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

const props = defineProps({
    year: String,
    packages: Object,
    hotelData: Object,
    countries: Object,
    flash: Object,
    user: Object,
    childCount: Number,
    infantCount: Number,
    noofadults: Number,
    nights: Number,
    noofrooms: Number,
});
let isLoading = ref(false);
const urlParams = new URLSearchParams(window.location.search);
dayjs.extend(advancedFormat);

const checkin = ref({
    allowInput: true,
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
    minDate: 'today',
});
const checkout = ref({
    allowInput: true,
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

onMounted(() => {
    addChildrenAge();
});

function handleDateChange() {
    checkout.value = {
        ...checkout.value,
        minDate: BookSearchform.checkin
    };
}

// id=childrenage should be added dynamically depending on the number of children. function to do this.
function addChildrenAge() {
    const roomstemparr = BookSearchform.children_age;
    BookSearchform.children_age = [];
    for (let i = 0; i < BookSearchform.noof_children; i++) {
        BookSearchform.children_age.push({
            age: 0,
        });
    }
    //preventing the data loss when the user changes the number of rooms
    for (let i = 0; i < roomstemparr.length; i++) {
        if (i < BookSearchform.children_age.length) {
            BookSearchform.children_age[i] = roomstemparr[i];
        }
    }
}

//function to calculate price based on the number of nights and number of adults and children and infants
function calculatePrice(adultprice, childprice, infantprice) {
    const nights = props.nights;
    const price = (nights * (props.noofadults * adultprice + props.childCount * childprice + props.infantCount * infantprice) * props.noofrooms);
    return price;
}

//function to format price with commas and currency symbol and round off to 2 decimal places
function formatPrice(price) {
    return price.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

//function to calculate the number of nights
function calculateNights() {
    const checkinDate = new Date(BookSearchform.checkin);
    const checkoutDate = new Date(BookSearchform.checkout);
    const diffTime = Math.abs(checkoutDate - checkinDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

const children_age = [];

for (let params of urlParams.entries()) {
    if (params[0].startsWith('children_age')) {
        children_age.push({
            age: params[1]
        });
    }
}

const BookSearchform = useForm({
    staytype: urlParams.get('staytype'),
    hotel: urlParams.get('hotel'),
    checkin: urlParams.get('checkin'),
    checkout: urlParams.get('checkout'),
    adults: urlParams.get('adults'),
    noof_children: urlParams.get('noof_children'),
    no_rooms: urlParams.get('no_rooms'),
    children_age,
    nationality: urlParams.get('nationality')
});

const BookSearchFormSubmit = () => {
    BookSearchform.get('/bookings/searchForPackages', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "vue-loading-overlay/dist/vue-loading.css";
@import 'vue3-carousel/dist/carousel.css';
@import 'flatpickr/dist/themes/material_orange.css';

.carousel__item {
    min-height: 400px;
    width: 100%;
    background-color: #ffffff;
    color: #FFFFFF;
    font-size: 20px;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.carousel__slide {
    padding: 10px;
}

.carousel__prev,
.carousel__next {
    box-sizing: content-box;
    border: 5px solid white;
}
</style>
<template>
    <AppLayout title="Bookings Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="nav-align-top mb-3 mt-4">

                <div id="accordionPopoutIcon" class="accordion mt-3 accordion-popout">

                    <div class="accordion-item card active">
                        <h2 class="accordion-header text-body d-flex justify-content-between" id="accordionPopoutIconOne">
                            <button type="button" class="accordion-button collapsed" data-bs-toggle="collapse"
                                data-bs-target="#accordionPopoutIcon-1" aria-controls="accordionPopoutIcon-1"
                                aria-expanded="true">
                                <i class="bx bx-search-alt me-2"></i>
                                Modify Search
                            </button>
                        </h2>

                        <div id="accordionPopoutIcon-1" class="accordion-collapse collapse show"
                            data-bs-parent="#accordionPopoutIcon" style="">
                            <div class="accordion-body">
                                <form @submit.prevent="BookSearchFormSubmit">
                                    <div class="row g-3">
                                        <div class="col-md-4">
                                            <label class="form-label" for="formtabs-first-name">Stay Type</label>
                                            <select class="form-select" v-model="BookSearchform.staytype" required>
                                                <option value="0">Single Stay</option>
                                                <option value="1">Split Stay (Same Hotel)</option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label" for="formtabs-last-name">Hotel</label>
                                            <select class="form-select" v-model="BookSearchform.hotel" required>
                                                <option value="all">All Hotels</option>
                                                <option v-for="hotel in hotelData" :value="hotel.id">{{ hotel.name }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label" for="formtabs-last-name">Nationality</label>
                                            <select class="form-select" v-model="BookSearchform.nationality" required>
                                                <option value="0" selected disabled>Select Nationality...</option>
                                                <option v-for="country in countries" :value="country.id">{{
                                                    country.name
                                                }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-md-3">
                                            <label class="form-label" for="formtabs-country">Check-in</label>
                                            <FlatPickr :config="checkin" v-model="BookSearchform.checkin"
                                                class="form-control" placeholder="Check-in Date" @change="handleDateChange"
                                                required />
                                        </div>

                                        <div class="col-md-3">
                                            <label class="form-label" for="formtabs-country">Check-out</label>
                                            <FlatPickr :config="checkout" v-model="BookSearchform.checkout"
                                                class="form-control" placeholder="Check-out Date" required />

                                            <span class="badge bg-label-danger mt-2"
                                                v-if="BookSearchform.checkout && BookSearchform.checkin"><i
                                                    class="bx bxs-calendar"></i>
                                                {{ calculateNights() }} Nights</span>
                                        </div>
                                        <div class="col-md-2">
                                            <label class="form-label" for="adults">Adults</label>
                                            <select class="form-select" required="required" v-model="BookSearchform.adults">
                                                <option value="" disabled="disabled">Number of Adults</option>
                                                <option value="1">1 Adult</option>
                                                <option value="2" selected="selected">2 Adults</option>
                                                <option value="3">3 Adults</option>
                                                <option value="4">4 Adults</option>
                                            </select>
                                        </div>
                                        <div class="col-md-2">
                                            <label class="form-label" for="formtabs-last-name">Kids</label>
                                            <select name="noof_children" class="form-select mb-2"
                                                v-model="BookSearchform.noof_children" @change="addChildrenAge">
                                                <option :value="0">No Children</option>
                                                <option :value="1">1 Child</option>
                                                <option :value="2">2 Children</option>
                                                <option :value="3">3 Children</option>
                                            </select>

                                            <span id="childrenage" v-if="BookSearchform.noof_children > 0"
                                                v-for="(room, index) in BookSearchform.children_age" :key="index">
                                                <select class="form-select mb-2" v-model="room.age" required>
                                                    <option value="needed" selected disabled>Age needed</option>
                                                    <option value="0">0 year old</option>
                                                    <option value="1">1 Year</option>
                                                    <option value="2">2 Years</option>
                                                    <option value="3">3 Years</option>
                                                    <option value="4">4 Years</option>
                                                    <option value="5">5 Years</option>
                                                    <option value="6">6 Years</option>
                                                    <option value="7">7 Years</option>
                                                    <option value="8">8 Years</option>
                                                    <option value="9">9 Years</option>
                                                    <option value="10">10 Years</option>
                                                    <option value="11">11 Years</option>
                                                    <option value="12">12 Years</option>
                                                    <option value="13">13 Years</option>
                                                    <option value="14">14 Years</option>
                                                    <option value="15">15 Years</option>
                                                    <option value="16">16 Years</option>
                                                    <option value="17">17 Years</option>
                                                </select>
                                            </span>
                                        </div>
                                        <div class="col-md-2">
                                            <label class="form-label" for="formtabs-last-name">Number of rooms</label>
                                            <select class="form-select" v-model="BookSearchform.no_rooms"
                                                required="required">
                                                <option value="" disabled="disabled" selected="selected">Number of Rooms
                                                </option>
                                                <option value="1">1 Room</option>
                                                <option value="2">2 Rooms</option>
                                                <option value="3">3 Rooms</option>
                                                <option value="4">4 Rooms</option>
                                                <option value="5">5 Rooms</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="pt-4">
                                        <button type="submit" class="btn btn-primary me-sm-3 me-1">Search
                                            Packages</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row g-3 package-grid-item-wrapper" style="margin-left: 10px;margin-right: 10px;">
                <div v-if="!props.packages.length == 0" class="col-md-3 mb-20" v-for="items in packages">
                    <div class="package-grid-item">
                        <div class="image">
                            <img :src="'/fetchImages/d?id=' + items.hotel.id + '&type=cover&w=445&h=223&fit=crop&border=400,1000,overlay'"
                                :alt="items.hotel.name" class="img-fluid" />
                        </div>
                        <div class="content clearfix">
                            <h6 class="box-title">{{ items.hotel.name }}<small>{{ items.hotel?.city?.name }}, {{
                                items.hotel?.country?.name
                            }}</small></h6>
                            <div class="rating-wrapper">
                                <div class="raty-wrapper">
                                    <i v-for="n in items.hotel.star_rating" class="bx bxs-star text-warning"></i>
                                </div>
                            </div>
                            <div class="absolute-in-content">
                                <span class="price">
                                    <small>Total</small>
                                    {{ formatPrice(calculatePrice(items.adult_rate, items.child_rate,
                                        items.infant_rate)) }}
                                </span>
                            </div>
                        </div>
                        <div class="text-center">
                            <div class="time">
                                <span><strong>{{ items.package_name }}</strong> </span><br>
                                <i class="bx bx-time text-warning"></i>
                                <span class="p-1">{{ items.minimum_stay + 1 }} days &amp;
                                    {{ items.minimum_stay }} nights</span>
                            </div>
                        </div>
                        <div style="border-bottom:1px dashed rgb(207 207 207);"></div>
                        <div class="d-grid p-2">
                            <button class="btn btn-info btn-toggle-sidebar">
                                <span class="align-middle"><strong>BOOK NOW</strong></span>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else class="alert alert-danger" role="alert">Sorry, we did not find any hotel for your search.
                    Please change your search criteria and try
                    again.<br> Thank you!</div>
            </div>
        </div>
    </AppLayout>
</template>